import { useEffect, useState } from "react";
import { Banner, Button } from "@praesto/ui-kit";
import { endImpersonation, getSuperadminApi } from "@praesto/core";
import { useCore } from "../lib/core";
import { decodeIdTokenClaims } from "../lib/jwt";
import { parseTenantSlug } from "../boot";

interface ImpersonationClaims {
  impersonatedBy: string;
  impersonationStart: number;
  impersonationTtl: number;
  impersonationExpiresAt: number;
  sub: string;
}

export function ImpersonationBanner() {
  const core = useCore();
  const [claims, setClaims] = useState<ImpersonationClaims | null>(null);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    (async () => {
      let tok: string;
      try {
        tok = await core.auth.getIdToken();
      } catch {
        return;
      }
      const parsed = decodeIdTokenClaims<Partial<ImpersonationClaims>>(tok);
      if (
        parsed?.impersonatedBy &&
        parsed.impersonationExpiresAt &&
        parsed.impersonationStart &&
        parsed.impersonationTtl &&
        parsed.sub
      ) {
        setClaims({
          impersonatedBy: parsed.impersonatedBy,
          impersonationStart: parsed.impersonationStart,
          impersonationTtl: parsed.impersonationTtl,
          impersonationExpiresAt: parsed.impersonationExpiresAt,
          sub: parsed.sub,
        });
      }
    })().catch(() => undefined);
  }, [core]);

  useEffect(() => {
    if (!claims) return;
    const tick = (): boolean => {
      const r = Math.max(0, (claims.impersonationExpiresAt - Date.now()) / 1000);
      setRemaining(r);
      return r > 0;
    };
    if (!tick()) return;
    const h = setInterval(() => {
      if (!tick()) clearInterval(h);
    }, 1000);
    return () => clearInterval(h);
  }, [claims]);

  if (!claims) return null;

  const min = Math.floor(remaining / 60);
  const sec = Math.floor(remaining % 60);
  const expired = remaining <= 0;

  async function exit() {
    if (!claims) return;
    const tenantSlug = parseTenantSlug(window.location.pathname) ?? "";
    const api = getSuperadminApi();
    try {
      const tok = await api.getAuthToken();
      await endImpersonation(
        api.apiBase,
        { targetTenantId: tenantSlug, targetUid: claims.sub },
        tok,
      );
    } catch {
      // Fire-and-forget: the token is expiring anyway and the server-side
      // revoke happens on the endImpersonation handler. Continue to sign
      // out the client below.
    }
    await core.auth.signOut();
    window.location.href = "/";
  }

  // Strong danger Banner, sticky, WITHOUT dismiss (C3F v18): a dangerous
  // state stays visible until the state itself ends. The Banner redeclares
  // its whole ink family, so the prop-less secondary button stays legible.
  return (
    <Banner tone="danger" strong sticky>
      <span className="text-mono-xs">
        Superadmin mode &middot; operatore: <strong>{claims.impersonatedBy}</strong>
        {expired ? (
          " · scaduta"
        ) : (
          <>
            {" · scadenza tra "}
            {/* A number is a reading unit: it never wraps (C3F v18). */}
            <span className="whitespace-nowrap">
              {min}m {sec}s
            </span>
          </>
        )}
      </span>
      <span className="ml-3 inline-flex">
        <Button size="sm" variant="secondary" onClick={() => void exit()}>
          Esci impersonation
        </Button>
      </span>
    </Banner>
  );
}
